<template>
  <div class="visual-edit-aside">
    <v-container fluid grid-list-md class="visual-edit-aside-container">
      <v-layout row wrap>
        <!--editor info-->
        <v-flex xs12>
          <v-card>
            <v-card-text>
              <div>Zoom level: {{zoomLevelRounded}}</div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!--update visual-->
        <v-flex xs12>
          <v-card>
            <v-card-text>
              <h4>Update visual</h4>
              <v-layout>
                <div class="ret-input-group flex-fill">
                  <v-text-field v-model="visual.name"
                                :label="$t('visuals.scheme-name')"
                                hide-details
                                data-test="name"
                  />
                  <v-btn text icon
                         class="ret-group-button"
                         @click="saveVisual"
                          data-test="save"
                  >
                    <v-icon>save</v-icon>
                  </v-btn>
                </div>

                <v-btn text color="warn" @click="onDestroy(visual)">{{ $t('archive') }}</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!--update text element-->
        <v-flex xs12 v-if="selectedElement && selectedElement.elementType.name === 'Text'">
          <v-form v-model="elementFormValid">
            <v-card>
              <v-card-text>
                <h4>Update element</h4>
                <v-layout>
                  <div class="ret-input-group flex-fill">
                    <v-flex xs12 md8>
                      <v-textarea
                        :label="$t('text')"
                        v-model="selectedElement.meta.text"
                        :rows="1"
                        :auto-grow="true"
                        :rules="elementTextRules"
                      />
                    </v-flex>
                    <v-flex xs12 md2>
                      <v-text-field
                        v-model="selectedElement.meta.fontSize"
                        :label="$t('size')"
                        :rules="fontSizeRules"
                      />
                    </v-flex>
                    <v-flex xs12 md2>
                      <v-select
                        v-model="selectedElement.meta.horizontalAlign"
                        :label="$t('align')"
                        :items="alignments"
                        hide-details
                      />
                    </v-flex>
                  </div>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-form>
        </v-flex>

        <!--levels-->
        <v-flex xs12>
          <levels-in-visual
            :visual="visual"
            :reload="reload"
            :edited-levels="editedLevels"
            :reselect-edited-level="reselectEditedLevel"
            :displayed-levels="displayedLevels"
            :reselect-displayed-levels="reselectDisplayedLevels"
          />
        </v-flex>

        <!--create new level-->
        <v-flex xs12>
          <v-card>
            <v-card-text>
              <h4>Create new level</h4>
              <div class="ret-input-group">
                <v-text-field v-model="level.name"
                              :label="$t('name')"
                              hide-details
                              data-test="level"
                />
                <v-btn text icon
                       class="ret-group-button"
                       :disabled="!level.name"
                       @click="createLevel"
                       data-test="create"
                >
                  <v-icon>library_add</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!--delete level-->
        <v-flex xs12>
          <v-card>
            <v-card-text>
              <h4>Delete level</h4>
              <div class="ret-input-group">
                <v-select
                  :items="levels"
                  item-text="name"
                  item-value="id"
                  v-model="levelToDelete"
                  clearable
                  label="Level"
                  single-line
                  hide-details
                />
                <v-btn text icon
                  class="ret-group-button"
                  :disabled="!levelToDelete"
                  @click="deleteLevel"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12>
          <p style="height: 120px;" />
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<style scoped>
.visual-edit-aside {
  position: fixed;
  top: 136px;
  right: 8px;
  width: 620px;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-right: 0;
  height: 100%;
  overflow-y: auto;
}

.visual-edit-aside-container {
  padding: 12px;
}

.flex-fill {
  flex: 1;
}
</style>

<script>
import LevelsInVisual from './levels-in-visual.vue'
import itemHelpers from '../../helpers/item-helpers.es6'
import isEqual from 'lodash.isequal'

const debounceDelay = 250

export default {
  mixins: [itemHelpers],
  components: {
    LevelsInVisual
  },
  props: {
    visual: {
      type: Object,
      required: true
    },
    zoomLevel: {
      type: Number,
      required: true
    },
    reload: {
      type: Function,
      required: true
    },
    editedLevels: {
      type: Array,
      required: true
    },
    reselectEditedLevel: {
      type: Function,
      required: true
    },
    displayedLevels: {
      type: Array,
      required: true
    },
    reselectDisplayedLevels: {
      type: Function,
      required: true
    },
    updateManyElements: {
      type: Function,
      required: true
    },
    selectedElement: {
      type: Object
    }
  },
  data () {
    return {
      model: { name: 'visuals' },
      level: {},
      levelToDelete: null,
      elementFormValid: false,
      elementTextRules: [
        v => !!v || 'Required',
        v => v.trim().length >= 1 || 'Must have at least 1 character'
      ],
      fontSizeRules: [
        v => !!v || 'Required',
        v => String(v) === String(parseInt(v)) || 'Must be an integer',
        v => parseInt(v) >= 6 || 'Must be at least 6',
        v => parseInt(v) <= 200 || 'Must be at most 200'
      ],
      alignments: [
        { value: 'left', text: this.$t('left') },
        { value: 'center', text: this.$t('center') },
        { value: 'right', text: this.$t('right') }
      ]
    }
  },
  computed: {
    levels () {
      return this.$store.state.items.levels
    },
    zoomLevelRounded () {
      return Math.round(this.zoomLevel * 1000) / 1000
    }
  },
  watch: {
    'selectedElement.meta': {
      handler (meta) {
        if (this.selectedElement && this.selectedElement.elementType.name === 'Text' && meta) {
          const { text, fontSize, horizontalAlign } = meta
          if (!isEqual({ text, fontSize, horizontalAlign }, this.previousMeta)) {
            this.debouncedSaveElement()
            this.previousMeta = { text, fontSize, horizontalAlign }
          }
        }
      },
      deep: true
    }
  },
  methods: {
    saveVisual () {
      this.$store.dispatch('updateItem', { modelName: 'visuals', item: this.visual })
    },
    debouncedSaveElement () {
      const { timeout } = this
      if (timeout) {
        window.clearTimeout(timeout)
      }
      this.timeout = window.setTimeout(this.saveElement.bind(this), debounceDelay)
    },
    saveElement () {
      if (this.elementFormValid) {
        this.updateManyElements([this.selectedElement])
      }
    },
    async createLevel () {
      await this.$store.dispatch('updateItem', { modelName: 'levels', item: this.level })
      this.level = {}
      this.$store.dispatch('getItems', { model: { name: 'levels' } })
    },
    async deleteLevel () {
      await this.$store.dispatch('destroyItem', { model: { name: 'levels' }, id: this.levelToDelete })
      this.levelToDelete = null
      this.$store.dispatch('getItems', { model: { name: 'levels' } })
    }
  }
}
</script>
